import { type CSSProperties, useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../shared/types/common";
import { ROUTE_STAFF_EXPORT } from "../../shared/routes/private.routes";
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CloseOutlined,
  CalendarOutlined,
  CheckOutlined,
  TableOutlined
} from "@ant-design/icons";
import { Switch, Tooltip } from "antd";
import { Checkbox, Space, Button, Col, Row, Modal, Drawer, Tag, Popover, Table } from "antd";
import { BiCloudDownload, BiCoinStack, BiFilterAlt, BiTransfer } from "react-icons/bi";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import {
  approveStaff,
  deleteStaff,
  getStaffList,
  rejectStaff,
} from "../../core/store/reducers/staff.slice";
import CreateEditStaffComponent from "./components/create-edit-staff.component";
import {
  accessAllowed,
  formatCost,
  formatCost1,
  formatDate,
  showNotification,
} from "../../shared/utils/utils";
import moment from "moment";
import { Client } from "../../core/api-client";
import {
  API_GET_ALL_DEPARTMENT_LIST,
  API_GET_ALL_PROJECT_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_ALL_VENDOR_LIST,
  API_GET_STAFF_LIST,
  API_GET_PROJECT_DETAILS,
  ROUTE_MANAGE_STAFF,
  API_GET_STAFF_TABLE_PREFERENCES,
  API_SAVE_STAFF_TABLE_PREFERENCES,
} from "../../shared/routes/private.routes";
import StaffBulkImportComponent from "./components/staff-bulk-import.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { RiCurrencyLine } from "react-icons/ri";
import StaffSalaryRevisionComponent from "./components/staff-salary-revision.component";
import { allPermissions } from "../../shared/constants/constants";
import Papa from "papaparse";
import { requestGetByModuleCommon } from "../../core/store/reducers/master.slice";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import StaffAssignmentComponent from "../project/components/staff-assignment.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import StaffLopComponent from "./components/staff-lop.component";

const FILTER_FIELD_LABELS: Record<string, string> = {
  staffName: "Name",
  staffStatusId: "Status",
  departmentId: "Department",
  projectId: "Project",
  lineManagerId: "Line Manager",
};

const STAFF_TABLE_NOWRAP_STYLE: CSSProperties = {
  whiteSpace: "nowrap",
  wordBreak: "keep-all",
  width: "max-content",
  minWidth: "max-content",
};
const STAFF_TABLE_NOWRAP_CONTENT_STYLE: CSSProperties = {
  ...STAFF_TABLE_NOWRAP_STYLE,
  display: "inline-block",
};

const withStaffNoWrapColumns = (columns: any[]) =>
  columns.map((column) => {
    const skipContentWrap = column.key === "actions";
    return {
      ...column,
      ellipsis: skipContentWrap ? column.ellipsis : false,
      width: skipContentWrap ? column.width : undefined,
      title:
        !skipContentWrap && typeof column.title === "string" ? (
          <span style={STAFF_TABLE_NOWRAP_CONTENT_STYLE}>{column.title}</span>
        ) : (
          column.title
        ),
      render: skipContentWrap
        ? column.render
        : (...args: any[]) => (
          <span style={STAFF_TABLE_NOWRAP_CONTENT_STYLE}>
            {column.render ? column.render(...args) : args[0]}
          </span>
        ),
      onCell: (...args: any[]) => {
        const cellProps = column.onCell?.(...args) || {};
        return {
          ...cellProps,
          style: {
            ...cellProps.style,
            ...STAFF_TABLE_NOWRAP_STYLE,
          },
        };
      },
      onHeaderCell: (...args: any[]) => {
        const headerProps = column.onHeaderCell?.(...args) || {};
        return {
          ...headerProps,
          style: {
            ...headerProps.style,
            ...STAFF_TABLE_NOWRAP_STYLE,
          },
        };
      },
    };
  });


function buildFilterValueText(
  key: string,
  value: any,
  lists: {
    departmentList: any[];
    projectList: any[];
    staffDropdownList: any[];
  }
): string {
  const { departmentList, projectList, staffDropdownList } = lists;
  switch (key) {
    case "staffName":
      return String(value).trim();
    case "staffStatusId":
      return Array.isArray(value) ? value.join(", ") : String(value);
    case "departmentId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            departmentList.find((d) => d.departmentId === id)
              ?.departmentName ?? id
        )
        .join(", ");
    }
    case "projectId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            projectList.find((p) => p.projectId === id)?.projectName ?? id
        )
        .join(", ");
    }
    case "lineManagerId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            staffDropdownList.find((s) => s.staffId === id)?.staffName ?? id
        )
        .join(", ");
    }
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

const CLEAR_FIELD_VALUES: Record<string, any> = {
  staffName: undefined,
  staffStatusId: [],
  departmentId: [],
  projectId: [],
  lineManagerId: [],
};

const APPROVE_BUTTON_STYLE: CSSProperties = {
  background: "var(--success)",
  borderColor: "var(--success)",
  color: "#ffffff",
  width: 32,
  height: 32,
  minWidth: 32,
};

const REJECT_BUTTON_STYLE: CSSProperties = {
  background: "#ff4d4f",
  borderColor: "#ff4d4f",
  color: "#ffffff",
  width: 32,
  height: 32,
  minWidth: 32,
};
const STAFF_PAGE_SIZE = 15;
const STAFF_DEFAULT_VISIBLE_COLUMNS = [
  "employeeId",
  "staffName",
  "lineManager",
  "staffStatusId",
  "employmentModeName",
  "role",
  "accountName",
];
type CurrentMonthAllocationRow = {
  projectId: string;
  projectName: string;
  percentAllocation: number;
  accountName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

type CurrentMonthProjectCellProps = {
  record: any;
  canOpenResourceAssignment?: boolean;
  onOpenProjectResourceAssignment?: (projectId: string) => void;
};

/** Project column: top allocation this month + popover with full breakdown from staff list API. */
function CurrentMonthProjectCell({
  record,
  canOpenResourceAssignment,
  onOpenProjectResourceAssignment,
}: CurrentMonthProjectCellProps) {
  const allocations = record.currentMonthAllocationProjects as
    | CurrentMonthAllocationRow[]
    | undefined;
  const topName = record.currentMonthTopProjectName as string | undefined;
  const lopPercent = Number(record.currentMonthLopPercent || 0);
  const benchPercent = Number(record.currentMonthBenchPercent || 0);
  const fallback = record.projectName as string | undefined;
  const label = (topName || fallback || "").trim();
  const monthLabel = moment().format("MMM YYYY");

  const popoverContent =
    (allocations && allocations.length > 0) || lopPercent > 0 || benchPercent > 0 ? (
      <div style={{ maxWidth: 720 }}>
        <div
          style={{
            marginBottom: 8,
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          Project allocations ({monthLabel})
        </div>
        <Table<CurrentMonthAllocationRow>
          size="small"
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey={(row, index) => `${row.projectId}-${index}`}
          dataSource={allocations || []}
          columns={[
            {
              title: "Client",
              dataIndex: "accountName",
              ellipsis: true,
              width: 140,
              render: (v: string | null | undefined) => v?.trim() || "—",
            },
            {
              title: "Project",
              dataIndex: "projectName",
              ellipsis: true,
              width: 160,
            },
            {
              title: "% allocation",
              width: 96,
              align: "right",
              render: (_: unknown, row) =>
                `${Number(row.percentAllocation ?? 0)}%`,
            },
            {
              title: "Project start",
              width: 118,
              render: (_: unknown, row) =>
                row.startDate
                  ? formatDate(row.startDate, "DD-MMM-YYYY")
                  : "—",
            },
            {
              title: "Project end",
              width: 118,
              render: (_: unknown, row) =>
                row.endDate ? formatDate(row.endDate, "DD-MMM-YYYY") : "—",
            },
            ...(canOpenResourceAssignment && onOpenProjectResourceAssignment
              ? [
                {
                  title: "",
                  key: "resourceAssignment",
                  width: 44,
                  align: "center" as const,
                  render: (_: unknown, row: CurrentMonthAllocationRow) => (
                    <Button
                      type="link"
                      size="small"
                      className="p-0"
                      icon={<UserOutlined />}
                      title="Resource assignment"
                      aria-label="Open resource assignment for this project"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenProjectResourceAssignment(row.projectId);
                      }}
                    />
                  ),
                },
              ]
              : []),
          ]}
        />
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <Tag color={lopPercent > 0 ? "warning" : "default"}>
            LOP: {lopPercent}%
          </Tag>
          <Tag color="default">Bench: {benchPercent}%</Tag>
        </div>
      </div>
    ) : null;

  return (
    <Space size={4} align="center" wrap>
      <span>{label || "—"}</span>
      {popoverContent ? (
        <Popover content={popoverContent} trigger="click" placement="leftTop">
          <Button
            type="text"
            size="small"
            className="p-0"
            icon={
              <InfoCircleOutlined style={{ color: "var(--primary)" }} />
            }
            aria-label={`Project allocations for ${monthLabel}`}
            onClick={(e) => e.stopPropagation()}
          />
        </Popover>
      ) : null}
    </Space>
  );
}

const ManageStaffPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingStaff, staffList, pagination } = useSelector(
    (state: RootState) => state.staff
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [staffIdToDelete, setStaffIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  // List local state
  const [staffListData, setStaffListData] = useState([] as any);

  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [profileOpeningStaffId, setProfileOpeningStaffId] = useState<string | null>(null);
  const [staffDropdownList, setStaffList] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [openSalaryRevision, setOpenSalaryRevision] = useState(false);
  const [employeeTypes, setEmployeeTypes] = useState<any[]>([]);
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [openStaffAssignment, setOpenStaffAssignment] = useState(false);
  const [resourceAssignmentProject, setResourceAssignmentProject] =
    useState<any>(null);
  const [openStaffLop, setOpenStaffLop] = useState(false);
  const [pageSize, setPageSize] = useState(STAFF_PAGE_SIZE);

  // Actions and selections
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "DESC" });
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [preferencesCanSave, setPreferencesCanSave] = useState(false);
  const skipNextPreferenceSave = useRef(true);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => [...STAFF_DEFAULT_VISIBLE_COLUMNS]);
  useEffect(() => {
    let cancelled = false;

    const loadPreferences = async () => {
      try {
        const response = await Client.get(API_GET_STAFF_TABLE_PREFERENCES);
        const savedPreferences = response?.data?.data;

        if (cancelled) return;
        setPreferencesCanSave(true);

        if (savedPreferences && typeof savedPreferences === "object") {
          if (Array.isArray(savedPreferences.visibleColumns)) {
            const validColumns = savedPreferences.visibleColumns.filter(
              (column: unknown): column is string =>
                typeof column === "string" &&
                STAFF_DEFAULT_VISIBLE_COLUMNS.includes(column)
            );

            setVisibleColumns(
              validColumns.length > 0
                ? validColumns
                : [...STAFF_DEFAULT_VISIBLE_COLUMNS]
            );
          }

          if (
            savedPreferences.filters &&
            typeof savedPreferences.filters === "object" &&
            !Array.isArray(savedPreferences.filters)
          ) {
            setFilters(savedPreferences.filters);
            form.setFieldsValue(savedPreferences.filters);
          }
        }
      } catch (error) {
        console.error("Unable to load Staff table preferences:", error);
      } finally {
        if (!cancelled) {
          setPreferencesLoaded(true);
        }
      }
    };

    void loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [form]);
  useEffect(() => {
    if (!preferencesLoaded || !preferencesCanSave) {
      return;
    }

    // Skip the first save after loading preferences.
    if (skipNextPreferenceSave.current) {
      skipNextPreferenceSave.current = false;
      return;
    }

    const savePreferences = async () => {
      try {
        await Client.put(API_SAVE_STAFF_TABLE_PREFERENCES, {
          preferences: {
            visibleColumns,
            filters,
          },
        });
      } catch (error) {
        console.error("Unable to save Staff table preferences:", error);
      }
    };

    void savePreferences();
  }, [
    preferencesLoaded,
    preferencesCanSave,
    visibleColumns,
    filters,
  ]);

  useEffect(() => {
    loadList(1);
  }, [filters, sorter, pageSize]);
  const loadList = useCallback((page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pageSize,
      sort_by: sorter.sort_by || "",
      sort_type: sorter.sort_type || "DESC",
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getStaffList({ params: data, body: filter }));
  }, [dispatch, filters, pageSize, pagination?.page, sorter]
  );


  const handleOpenStaffAssignmentForProject = useCallback(
    async (projectId: string) => {
      try {
        const response = await Client.get(API_GET_PROJECT_DETAILS + projectId);
        const entity = response?.data?.data?.[0];
        if (entity) {
          setResourceAssignmentProject(entity);
          setOpenStaffAssignment(true);
        } else {
          showNotification("Project not found", "error");
        }
      } catch {
        showNotification("Could not load project", "error");
      }
    },
    []
  );

  const onCloseStaffAssignment = useCallback(() => {
    setOpenStaffAssignment(false);
    setResourceAssignmentProject(null);
  }, []);

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const bulkImportEntityListner = () => {
    setBulkImportOpen(true);
    // setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setSelectedEntity(entity);
    setCreateEditOpen(true);
  };

  /** Opens the staff profile (manage staff) screen for a row. */
  const openStaffProfile = (entity: any) => {
    if (!entity?.staffId) return;
    setProfileOpeningStaffId(entity.staffId);
    navigate(`${ROUTE_MANAGE_STAFF}/${entity.staffId}`, {
      state: { staff: entity },
    });
  };



  useEffect(() => {
    setStaffListData(
      staffList.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [staffList]);
  const onFilterApply = (values: any) => {
    setFilters((previous: any) => ({ ...values, includeInactive: previous.includeInactive }));
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => k !== "includeInactive" && isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: FILTER_FIELD_LABELS[key] || key,
        valueText: buildFilterValueText(key, filters[key], {
          departmentList,
          projectList,
          staffDropdownList,
        }),
      }));
  }, [filters, departmentList, projectList, staffDropdownList]);

  const clearAllFilters = useCallback(() => {
    setFilters((previous: any) => ({ includeInactive: previous.includeInactive }));
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in CLEAR_FIELD_VALUES ? CLEAR_FIELD_VALUES[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );
  const onPageChange = (pageNumber: number) => {
    // loadList((pageNumber - 1) * pagination?.limit);
    loadList(pageNumber);
  };
  const onPageSizeChange = (_pageNumber: number, selectedPageSize: number) => {
    setPageSize(selectedPageSize);
  };
  const hasAnyAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_STAFF) ||
    accessAllowed(myPermissions, allPermissions.DELETE_STAFF) ||
    accessAllowed(myPermissions, allPermissions.STAFF_ACTIVITY_LOGS) ||
    accessAllowed(myPermissions, allPermissions.VIEW_STAFF_SALARY_REVISION) ||
    accessAllowed(myPermissions, allPermissions.APPROVE_STAFF) ||
    accessAllowed(myPermissions, allPermissions.REJECT_STAFF);
  const canProjectStaffAssignment = accessAllowed(
    myPermissions,
    allPermissions.PROJECT_STAFF_ASSIGNMENT
  );
  const hasApproveRecords =
    accessAllowed(myPermissions, allPermissions.APPROVE_STAFF) &&
    staffListData.some((staff: any) => staff?.staffStatusId === "DRAFT");

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const hasRejectRecords =
    accessAllowed(myPermissions, allPermissions.REJECT_STAFF) &&
    staffListData.some((staff: any) => staff?.staffStatusId === "DRAFT");
  const columns = useMemo(
    () => [
      {
        title: "Employee Id",
        dataIndex: "employeeId",
        ...getSortableColumnProps("employeeId"),
        key: "employeeId",
        render: (employeeId: string, record: any) => (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openStaffProfile(record);
            }}
          >
            {employeeId}
          </a>
        ),
      },
      {
        title: "Staff Name",
        dataIndex: "staffName",
        ...getSortableColumnProps("staffName"),
        key: "staffName",
      },
      {
        title: "Line Manager",
        dataIndex: "lineManager",
        ...getSortableColumnProps("lineManager"),
        key: "lineManager",
      },
      {
        title: "Status",
        dataIndex: "staffStatusId",
        ...getSortableColumnProps("staffStatusId"),
        key: "staffStatusId",
        render: (status: string) => {
          const color =
            status === "ACTIVE"
              ? "success"
              : status === "DRAFT"
                ? "processing"
                : status === "NOTICE PERIOD"
                  ? "warning"
                  : "red";
          return status ? <Tag color={color}>{status}</Tag> : "—";
        },
      },
      {
        title: "Employment Mode",
        dataIndex: "employmentModeName",
        ...getSortableColumnProps("employmentModeName"),
        key: "employmentModeName",
      },
      {
        title: "Role",
        dataIndex: "role",
        ...getSortableColumnProps("role"),
        key: "role",
      },
      // {
      //   title: "Employee Type",
      //   dataIndex: "employeeType",
      //  
      //   key: "employeeType",
      // },
      // {
      //   title: "Department Name",
      //   dataIndex: "departmentName",
      //   : false,
      //   key: "departmentName",
      // },
      // {
      //   title: "Project",
      //   dataIndex: "projectName",
      //   : false,
      //   key: "projectName",
      //   render: (_: usorternknown, record: any) => (
      //     <CurrentMonthProjectCell
      //       record={record}
      //       canOpenResourceAssignment={canProjectStaffAssignment}
      //       onOpenProjectResourceAssignment={handleOpenStaffAssignmentForProject}
      //     />
      //   ),
      // },
      // {
      //   title: "CTC",
      //   dataIndex: "ctc",
      //   : false,
      //   key: "ctc",
      //   render: (ctc: number, record: any) => (ctc ? formatCost1(ctc,record.salaryCurrency) : ""),
      // },
      // {
      //   title: "Date of Joining",
      //   dataIndex: "doj",
      //   : false,
      //   key: "doj",
      //   render: (doj: string) => (doj ? formatDate(doj,"DD-MMM-YYYY ") : ""),
      // },
      // {
      //   title: "Last Working Day",
      //   dataIndex: "lwd",
      //   : false,
      //   key: "lwd",
      //   render: (lwd: string) => (lwd ? formatDate(lwd,"DD-MMM-YYYY ") : ""),
      // },
      {
        title: "Client",
        dataIndex: "accountName",
        ...getSortableColumnProps("accountName"),
        key: "accountName",
        render: (_: unknown, record: any) =>
          record?.currentMonthTopAccountName || "-",
      },
      ...(hasAnyAccess
        ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: "130px",
            align: "center",
            render: (text: any, record: any) => (
              <Space
                size={8}
                wrap={false}
                style={{ justifyContent: "center", width: "100%" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* {accessAllowed(myPermissions, allPermissions.UPDATE_STAFF) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                    title="Edit"
                  />
                )} */}
                {/* {accessAllowed(myPermissions, allPermissions.DELETE_STAFF) && (
                  <Button
                    type="link"
                    danger
                    onClick={() => handleDeleteModal(record?.staffId)}
                    icon={<DeleteOutlined />}
                    title="Delete"
                  />
                )} */}
                {accessAllowed(
                  myPermissions,
                  allPermissions.STAFF_ACTIVITY_LOGS
                ) && (
                    <Button
                      type="link"
                      onClick={() => handleOpenLogs(record)}
                      icon={<EyeOutlined />}
                      title="Activity log"
                    />
                  )}
                {/* {accessAllowed(
                  myPermissions,
                  allPermissions.VIEW_STAFF_SALARY_REVISION
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleSalaryRevision(record)}
                    icon={<RiCurrencyLine />}
                    title="Salary revision"
                  />
                )} */}
                {/* {accessAllowed(myPermissions, allPermissions.UPDATE_STAFF) && (
                  <Button
                    type="link"
                    onClick={() => handleStaffLop(record)}
                    icon={<CalendarOutlined />}
                    title="LOP"
                  />
                )} */}
              </Space>
            ),
          },
          ...(hasApproveRecords || hasRejectRecords
            ? [
              {
                title: "",
                key: "approve",
                fixed: "right",
                width: "56px",
                align: "center",
                render: (_text: any, record: any) => {
                  const canApproveStaff = accessAllowed(
                    myPermissions,
                    allPermissions.APPROVE_STAFF
                  );

                  const canRejectStaff = accessAllowed(
                    myPermissions,
                    allPermissions.REJECT_STAFF
                  );

                  if (record?.staffStatusId !== "DRAFT") {
                    return null;
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      {/* Approve */}
                      {canApproveStaff && (
                        <Button
                          shape="circle"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproveStaff(record);
                          }}
                          icon={<CheckOutlined />}
                          title="Approve"
                          style={APPROVE_BUTTON_STYLE}
                        />
                      )}

                      {/* Reject */}
                      {canRejectStaff && (
                        <Button
                          shape="circle"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRejectStaff(record);
                          }}
                          icon={<CloseOutlined />}
                          title="Reject"
                          style={REJECT_BUTTON_STYLE}
                        />
                      )}
                    </div>
                  );
                },
              }
            ]
            : []),
        ]
        : []),
    ],
    [sorter, hasAnyAccess, hasApproveRecords, hasRejectRecords, myPermissions]
  );
  const displayedColumns = useMemo(
    () =>
      columns.filter(
        (column: any) =>
          column.key === "actions" ||
          column.key === "approve" ||
          visibleColumns.includes(column.key)
      ),
    [columns, visibleColumns]
  );

  const handleApproveStaff = (record: any) => {
    Modal.confirm({
      title: "Confirm Approval",
      content: "Are you sure you want to approve this staff?",
      okText: "Approve",
      cancelText: "Cancel",
      onOk: () => {
        dispatch(approveStaff(record.staffId)).then((val: any) => {
          if (val?.payload?.data?.statusCode === 200) {
            showNotification("Staff Approved Successfully", "success");
            loadList();
          } else if (val?.payload?.data?.message) {
            showNotification(val.payload.data.message, "warning");
          }
        });
      },
    });
  };

  const handleRejectStaff = (record: any) => {
    Modal.confirm({
      title: "Reject Staff",
      content: "Are you sure you want to reject this Staff?",
      okText: "Reject",
      cancelText: "Cancel",
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        dispatch(rejectStaff(record.staffId)).then((val: any) => {
          if (val?.payload?.data?.statusCode === 200) {
            showNotification("Staff Rejected Successfully", "success");
            loadList();
          } else if (val?.payload?.data?.message) {
            showNotification(val.payload.data.message, "warning");
          }
        });
      },
    });
  };


  const handleDelete = (staffId: string) => {
    dispatch(deleteStaff(staffId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Staff Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleDeleteModal = (staffId: string) => {
    setDeleteModalVisible(true);
    setStaffIdToDelete(staffId);
  };

  const onDeleteConfirm = () => {
    handleDelete(staffIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setStaffIdToDelete("");
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSelectedStaffId(record.staffId);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setSelectedStaffId("");
  };

  const handleSalaryRevision = (record: any) => {
    setOpenSalaryRevision(true);
    setSelectedStaffId(record.staffId);
    setSelectedEntity(record);
  };

  const handleStaffLop = (record: any) => {
    setOpenStaffLop(true);
    setSelectedStaffId(record.staffId);
    setSelectedEntity(record);
  };

  const onCloseSalaryRevision = () => {
    setOpenSalaryRevision(false);
    setSelectedStaffId("");
  };

  // const handleDelete = (staffId: string)=>{
  //     dispatch(deleteStaff(staffId)).then(
  //         (val: any) => {
  //           if (val?.payload?.data) {
  //             showNotification("Staff Deleted", "success");
  //             loadList();
  //           }
  //         }
  //       );
  // }

  const onChangeHandler = (_pagination: any, _filters: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "", sort_type: "DESC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };
  const createEditSubmit = (staffId?: string) => {
    setCreateEditOpen(false);
    if (staffId) {
      navigate(`/manage-staff/${staffId}`);
    } else {
      loadList();
    }
  };
  useEffect(() => {
    // dispatch(getList()).then((val: any) => {
    //   if (val?.payload?.data?.data?.length > 0) {
    //     setList(
    //       val.payload.data.data.map((val: any) => {
    //         return { label: val.name, value: val._id };
    //       })
    //     );
    //   }
    // });
    loadList();
  }, []);
  const onValuesChange = (value: any, _allValues: any) => { };

  useEffect(() => {
    fetchStaffList();
    fetchProjectList();
    fetchDepartmentList();
    fetchEmployeeTypes()
    fetchVendorList()
  }, [filters.includeInactive]);

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST, filters.includeInactive ? { params: { includeInactive: true } } : {});
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  const fetchProjectList = async () => {
    const response = await Client.get(API_GET_ALL_PROJECT_LIST, filters.includeInactive ? { params: { includeInactive: true } } : {});
    if (response?.data?.data) {
      setProjectList(response?.data?.data);
    } else {
      setProjectList([]);
    }
  };

  const fetchDepartmentList = async () => {
    const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
    if (response?.data?.data) {
      setDepartmentList(response?.data?.data);
    } else {
      setDepartmentList([]);
    }
  };

  const staffDropdownOptions = staffDropdownList.map((staff) => ({
    value: staff?.staffId,
    label: staff?.staffName,
  }));

  const projectDropdownOptions = projectList.map((project) => ({
    value: project?.projectId,
    label: project?.projectName,
  }));

  const departmentDropdownOptions = departmentList.map((department) => ({
    value: department?.departmentId,
    label: department?.departmentName,
  }));
  const fetchEmployeeTypes = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_employee_type" })
      ).unwrap();
      // console.log(response.data);
      if (response?.success) {
        setEmployeeTypes(response?.data.items);
      }
    } catch (error) { }
  };
  const fetchVendorList = async () => {
    const response = await Client.get(API_GET_ALL_VENDOR_LIST);
    if (response?.data?.data) {
      setVendorList(response?.data?.data);
    } else {
      setVendorList([]);
    }
  };
  const exportStaffList = async () => {
    try {
      const data = {
        sort_by: sorter.sort_by || "",
        sort_type: sorter.sort_type || "DESC",
      };
      let filter = {} as any;
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          filter[key] = filters[key];
        }
      });

      const result = await Client.post(
        API_GET_STAFF_LIST,
        filter,
        { params: data },
      );

      // const result =  dispatch(getStaffList({ params: data, body: filter })); 
      const rows = result?.data?.data;
      console.log(rows);
      const rowData = rows?.map((item: any) => {
        return {
          "Entity": item?.entityName,
          "Employee Id": item?.employeeId,
          "Employee Name": item?.staffName,
          "Status": item?.staffStatusId,
          "Doj": item?.doj ? moment(item?.doj).format("DD-MMM-YYYY") : "",
          "Contract Start Date": item?.contractStartDate ? moment(item?.contractStartDate).format("DD-MMM-YYYY") : "",
          "Contract End Date": item?.contractEndDate ? moment(item?.contractEndDate).format("DD-MMM-YYYY") : "",
          "Email ID": item?.email,
          "Client": item?.accountName || item?.currentMonthTopAccountName || "-",
          "Project": item?.currentMonthTopProjectName ?? "-",
          "Designation": item?.designation,
          "Reporting Manager": item?.lineManager,
          "Employee Type": employeeTypes.find((employeeType: any) => employeeType.id === item?.employeeType)?.name || "", // convert to text
          "Employment Mode": item?.employmentModeName,
          "Vendor Name (for contractors)": vendorList.find((vendor: any) => vendor.vendorId === item?.vendorId)?.vendorName || "", // convert to text
          "Vendor EMP ID": item?.vendorEmpId || "",
          "Vendor Employment Type": item?.vendorEmploymentType || "",
          "Vendor Employment Mode": item?.vendorEmploymentMode || "",
          "LWD": item?.lwd ? moment(item?.lwd).format("DD-MMM-YYYY") : "",
          "Department": item?.departmentName,
          "Sub Department": item?.subDepartmentName,
          "Role": item?.role,
        }
      })
      const csv = Papa.unparse(rowData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "staff_list.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (e) {

    }
  };
  const formProps = {
    form: form,
    name: "manage-staff-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "staffName",
        label: "",
        placeholder: "Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "staffStatusId",
        label: "",
        placeholder: "Staff Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "DRAFT", label: "DRAFT" },
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
          { value: "NOTICE PERIOD", label: "NOTICE PERIOD" },
        ],
      },
      {
        name: "departmentId",
        label: "",
        placeholder: "Department",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: departmentDropdownOptions,
      },
      {
        name: "projectId",
        label: "",
        placeholder: "Project",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: projectDropdownOptions,
      },
      {
        name: "lineManagerId",
        label: "",
        placeholder: "Line Manager",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: staffDropdownOptions,
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };
  const headerButtons = (
    <Space size="middle" wrap className="app-page-actions">
      <Checkbox className="app-include-inactive" checked={!!filters.includeInactive}
        onChange={(event) => setFilters((previous: any) => ({ ...previous, includeInactive: event.target.checked }))}>
        Include Inactive
      </Checkbox>
      {accessAllowed(myPermissions, allPermissions.STAFF_BULK_UPLOAD) && (
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={bulkImportEntityListner}
        >
          <span className="font-size-14">
            {!screenType?.isPortrait && "Staff "}Bulk import
          </span>
        </Button>
      )}
      {accessAllowed(myPermissions, allPermissions.CREATE_STAFF) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Staff"}
          </span>
        </Button>
      )}
      {accessAllowed(myPermissions, allPermissions.CREATE_STAFF) && (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() =>
            navigate(ROUTE_STAFF_EXPORT, {
              state: {
                filters,
                sorter,
              },
            })
          }
        >
          <span className="font-size-14">
            Export {!screenType?.isPortrait && "Staff List"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
      <Tooltip title="Customize Table View">
        <Button
          shape="circle"
          type="primary"
          icon={<TableOutlined />}
          aria-label="Customize Table View"
          onClick={() => setColumnSettingsOpen(true)}
        />
      </Tooltip>

    </Space>
  );

  return (
    <div className="app-page">
      {/* Page header card */}
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <TeamOutlined />
              </div>
              <h1 className="app-page-title">Manage Staff</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage your organization’s staff and assignments
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {/* Filters card */}
      {filterVisible ? (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      ) : null}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      {/* Table card */}
      <div className="app-table-card milestones-table-card">
        <DataTableComponent
          pagination={true}
          loading={loadingStaff || Boolean(profileOpeningStaffId)}
          dataSource={staffListData}
          columns={withStaffNoWrapColumns(displayedColumns)}
          onChange={onChangeHandler}
          onRow={(record: any) => ({
            onClick: () => openStaffProfile(record),
            style: { cursor: "pointer" },
          })}
          defaultPage={0}
          currentPage={pagination.page}
          totalRows={pagination?.total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
          pageSizeOptions={["10", "15", "25", "50", "100"]}
          tableWidth="max-content"
        />
      </div>


      <Modal
        title="Customize Table View"
        open={columnSettingsOpen}
        onCancel={() => setColumnSettingsOpen(false)}
        width={560}
        footer={
          <Space>
            <Button
              onClick={() =>
                setVisibleColumns([...STAFF_DEFAULT_VISIBLE_COLUMNS])
              }
            >
              Reset to Default
            </Button>
            <Button
              type="primary"
              onClick={() => setColumnSettingsOpen(false)}
            >
              Done
            </Button>
          </Space>
        }
      >
        <div
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Row gutter={[24, 0]} style={{ margin: 0 }}>
            {columns
              .filter(
                (column: any) =>
                  column.key !== "actions" &&
                  column.key !== "approve"
              )
              .map((column: any) => {
                const checked = visibleColumns.includes(column.key);

                return (
                  <Col xs={24} sm={12} key={column.key}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "12px 0",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <span>
                        {typeof column.title === "string"
                          ? column.title
                          : String(column.key)}
                      </span>


                      <Switch
                        size="small"
                        checked={checked}
                        aria-label={String(column.title)}
                        disabled={
                          checked &&
                          visibleColumns.filter((key) =>
                            STAFF_DEFAULT_VISIBLE_COLUMNS.includes(key)
                          ).length === 1
                        }
                        onChange={(enabled) => {
                          setVisibleColumns((previous) => {
                            if (enabled) {
                              return previous.includes(column.key)
                                ? previous
                                : [...previous, column.key];
                            }

                            if (
                              previous.length <= 1 &&
                              previous.includes(column.key)
                            ) {
                              return previous;
                            }

                            return previous.filter(
                              (key) => key !== column.key
                            );
                          });
                        }}
                      />
                    </div>
                  </Col>
                );
              })}
          </Row>
        </div>
      </Modal>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this staff member?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditStaffComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {bulkImportOpen && (
        <StaffBulkImportComponent
          onClose={() => setBulkImportOpen(false)}
          visible={bulkImportOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedStaffId}
            record={selectedEntity}
            module="Staff"
          />
        </Drawer>
      )}
      {openSalaryRevision && (
        <StaffSalaryRevisionComponent
          visible={openSalaryRevision}
          onClose={onCloseSalaryRevision}
          selectedEntity={selectedEntity}
          onSubmit={() => loadList()}
        />
      )}
      {openStaffAssignment && resourceAssignmentProject && (
        <StaffAssignmentComponent
          visible={openStaffAssignment}
          onClose={onCloseStaffAssignment}
          selectedEntity={resourceAssignmentProject}
          onSubmit={() => loadList()}
        />
      )}
      {openStaffLop && (
        <StaffLopComponent
          visible={openStaffLop}
          onClose={() => setOpenStaffLop(false)}
          selectedEntity={selectedEntity}
          onSubmit={() => loadList()}
        />
      )}
    </div>
  );
};
export default ManageStaffPage;
